<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-800/60 p-5" @click.self="close" @keydown.esc.stop.prevent="close" @keydown.tab="trapFocus">
      <div ref="dialog" role="dialog" aria-modal="true" :aria-label="`${actionLabel} parent`" class="w-full max-w-xl overflow-hidden rounded-lg border border-gray-200 bg-white text-[11px] leading-4 shadow-xl">
        <div class="flex items-start justify-between gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
          <div class="min-w-0">
            <h3 class="text-xs font-semibold text-gray-900">{{ actionLabel }} parent</h3>
            <p class="mt-1 text-[11px] text-gray-600">{{ childIds.length }} selected entities</p>
          </div>
          <button ref="closeButton" type="button" class="bulk-button !w-6 !p-0" aria-label="Close bulk parent action" @click="close">
            <svg class="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path stroke-linecap="round" d="M6 6l12 12M6 18L18 6" /></svg>
          </button>
        </div>
        <div class="p-4">
          <LoadingIndicator v-if="loading" message="Loading parent entities…" />
          <form v-else @submit.prevent="submit" class="flex flex-col gap-3">
            <label class="bulk-label">Parent entity type
              <select v-model="selectedTypeId" class="bulk-input" required>
                <option :value="null">Select type…</option>
                <option v-for="type in entityTypes" :key="type.id" :value="type.id">{{ type.type_name }}</option>
              </select>
            </label>
            <label class="bulk-label">Parent entity
              <select v-model="selectedParentId" class="bulk-input sensitive" :disabled="!selectedTypeId || loadingParents" required>
                <option :value="null">{{ loadingParents ? 'Loading entities…' : 'Select parent…' }}</option>
                <option v-for="entity in parentEntities" :key="entity.id" :value="entity.id">{{ entity.name }}</option>
              </select>
            </label>
            <p v-if="error" role="alert" class="text-red-700">{{ error }}</p>
            <div class="flex justify-end gap-2 pt-2">
              <button type="button" class="bulk-button" :disabled="saving" @click="close">Cancel</button>
              <button type="submit" class="bulk-button bulk-primary" :disabled="saving || !selectedParentId">{{ saving ? `${actionLabel}ing…` : actionLabel }}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import axios from 'axios';
import abortable from '@/mixins/abortable';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'BulkEntityParentActionModal',
  components: { LoadingIndicator },
  mixins: [abortable],
  props: {
    childIds: { type: Array, required: true },
    action: { type: String, required: true, validator: value => ['add', 'remove'].includes(value) },
  },
  emits: ['completed', 'close'],
  data() {
    return { entityTypes: [], selectedTypeId: null, parentEntities: [], selectedParentId: null, loading: true, loadingParents: false, saving: false, error: '' };
  },
  computed: {
    actionLabel() { return this.action === 'add' ? 'Add' : 'Remove'; },
  },
  watch: {
    selectedTypeId() { this.loadParents(); },
  },
  async mounted() {
    this._returnFocus = document.activeElement;
    this._bodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const signal = this._renewAbortSignal();
    try {
      const { data } = await axios.get('/api/entity-types/', { signal });
      if (!signal.aborted) this.entityTypes = data;
    } catch (error) {
      if (!this._isAborted(error)) this.error = 'Could not load entity types.';
    } finally {
      if (!signal.aborted) { this.loading = false; await this.$nextTick(); this.$refs.closeButton?.focus(); }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = this._bodyOverflow || '';
    if (this._returnFocus?.isConnected) this._returnFocus.focus();
  },
  methods: {
    close() { if (!this.saving) this.$emit('close'); },
    trapFocus(event) {
      const elements = [...this.$refs.dialog.querySelectorAll('button:not(:disabled), select:not(:disabled)')];
      const first = elements[0]; const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    },
    async loadParents() {
      this.selectedParentId = null;
      this.parentEntities = [];
      if (!this.selectedTypeId) return;
      const signal = this._renewAbortSignal('parents');
      this.loadingParents = true;
      try {
        const { data } = await axios.get('/api/entities/', { signal, params: { entity_type_id: this.selectedTypeId } });
        const rows = Array.isArray(data) ? data : data.results || [];
        if (!signal.aborted) this.parentEntities = rows.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } catch (error) {
        if (!this._isAborted(error)) this.error = 'Could not load parent entities.';
      } finally {
        if (!signal.aborted) this.loadingParents = false;
      }
    },
    async submit() {
      if (!this.selectedParentId || this.saving) return;
      this.saving = true;
      this.error = '';
      try {
        const endpoint = this.action === 'add' ? 'bulk-add-children' : 'bulk-remove-children';
        await axios.post(`/api/entities/${this.selectedParentId}/${endpoint}/`, { child_ids: this.childIds });
        this.$emit('completed');
        this.$emit('close');
      } catch (error) {
        this.error = error.response?.data?.error || `Could not ${this.action} parent.`;
      } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
@reference "../../assets/theme.css";
.bulk-button { @apply inline-flex h-6 items-center justify-center gap-1 rounded border border-gray-300 bg-white px-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-neutral-500 disabled:opacity-50; }
.bulk-primary { @apply border-primary-500 bg-primary-500 text-white hover:bg-primary-600; }
.bulk-input { @apply h-7 w-full rounded border border-gray-300 bg-white px-2 text-[11px] text-gray-800 focus:outline-none focus:ring-1 focus:ring-neutral-500 disabled:opacity-50; }
.bulk-label { @apply flex flex-col gap-1 text-gray-600; }
</style>
